import Image, { type StaticImageData } from 'next/image';
import RiStore2Line from 'remixicon-react/Store2LineIcon';

import StorefrontLink from '@/common/components/storefront-link/storefront-link.component';

export interface ProductCardV2Props {
  image: string | StaticImageData;
  name: string;
  store: string;
  price: number;
  discount?: number;
  originalPrice?: number;
  imageAlt?: string;
  href?: string;
  priority?: boolean;
  onClick?: () => void;
}

const formatPrice = (price: number) =>
  new Intl.NumberFormat('fa-IR').format(price);

function ProductCardV2({
  image,
  name,
  store,
  price,
  discount,
  originalPrice,
  imageAlt,
  href,
  priority = false,
  onClick,
}: ProductCardV2Props) {
  const discountInfo =
    discount !== undefined &&
    discount > 0 &&
    originalPrice !== undefined &&
    originalPrice > price
      ? {
          discount,
          originalPrice,
        }
      : null;
  const content = (
    <>
      <div className="relative aspect-square w-full overflow-hidden rounded-[6px] bg-silver-200 md:rounded-[10px]">
        <Image
          src={image}
          alt={imageAlt ?? name}
          width={600}
          height={600}
          sizes="(max-width: 767px) 46vw, (max-width: 1280px) 18vw, 220px"
          priority={priority}
          className="h-full w-full object-cover"
        />
      </div>

      <div className="flex min-h-[113px] flex-1 flex-col pt-3 md:min-h-[140px]">
        <h3
          title={name}
          className="
            line-clamp-2 min-h-10
            text-xs font-medium leading-5 text-[#242424]
            md:text-sm md:font-semibold md:text-black-400
          "
        >
          {name}
        </h3>

        <div className="mt-2 flex items-center gap-1 text-gray-80 md:mt-3 md:gap-2">
          <RiStore2Line
            className="size-[14px] shrink-0 md:size-4"
            aria-hidden="true"
          />

          <span className="truncate text-[11px] leading-4 md:text-xs md:leading-5">
            {store}
          </span>
        </div>

        <div className="mt-auto flex min-h-[58px] flex-row-reverse items-start justify-between gap-2 pt-3">
          <div className="flex min-w-0 flex-col items-end">
            <div className="flex items-baseline gap-1 whitespace-nowrap">
              <span className="text-base font-bold leading-6 text-[#100F0E]">
                {formatPrice(price)}
              </span>

              <span className="text-2xs text-black-50">تومان</span>
            </div>

            {discountInfo && (
              <div className="mt-0.5 flex items-center gap-1 whitespace-nowrap">
                <span className="text-xs leading-5 text-gray-80 line-through">
                  {formatPrice(discountInfo.originalPrice)}
                </span>
                <span className="text-2xs text-black-50">تومان</span>
              </div>
            )}
          </div>

          {discountInfo && (
            <span
              className="
                flex h-5 w-[22px] shrink-0 items-center justify-center
                rounded bg-error-100 text-[11px] font-medium text-white
                md:h-[26px] md:w-6 md:rounded md:text-xs
              "
            >
              {formatPrice(discountInfo.discount)}٪
            </span>
          )}
        </div>
      </div>
    </>
  );

  return (
    <article
      dir="rtl"
      onClick={href ? undefined : onClick}
      className="
        flex h-full w-full cursor-pointer flex-col
        rounded-lg border-[0.5px] border-silver-400 bg-white p-2
        md:rounded-xl
      "
    >
      {href ? (
        <StorefrontLink
          href={href}
          className="flex h-full min-w-0 flex-col"
          aria-label={`خرید ${name}`}
        >
          {content}
        </StorefrontLink>
      ) : (
        content
      )}
    </article>
  );
}

export default ProductCardV2;
